import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuthStore } from "../store/authStore";
import { toast } from "sonner";

const C = {
  bg:'#0a0a0f', surface:'#13131f', input:'#1a1a2e',
  border:'rgba(124,58,237,0.2)', text:'#e8e8f0', muted:'#9090b0', hint:'#5a5a7a',
  violet:'#7c3aed', grad:'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  green:'#4ade80', red:'#f87171', blue:'#60a5fa',
};

function ScoreBadge({ score }) {
  const color = score >= 80 ? '#4ade80' : score >= 65 ? '#60a5fa' : score >= 50 ? '#fbbf24' : '#f87171';
  const bg = score >= 80 ? 'rgba(74,222,128,0.12)' : score >= 65 ? 'rgba(96,165,250,0.12)' : score >= 50 ? 'rgba(251,191,36,0.12)' : 'rgba(248,113,113,0.12)';
  return <span style={{ fontSize:14, fontWeight:800, padding:'3px 12px', borderRadius:6, background:bg, color }}>{score}/100</span>;
}

function GradeBadge({ grade }) {
  const colors = { "A+":"#4ade80", A:"#4ade80", B:"#60a5fa", C:"#fbbf24", D:"#fb923c", F:"#f87171" };
  const bgs = { "A+":"rgba(74,222,128,0.15)", A:"rgba(74,222,128,0.15)", B:"rgba(96,165,250,0.15)", C:"rgba(251,191,36,0.15)", D:"rgba(251,146,60,0.15)", F:"rgba(248,113,113,0.15)" };
  return (
    <span style={{ color:colors[grade]||C.muted, background:bgs[grade]||'rgba(144,144,176,0.15)', fontSize:20, fontWeight:900, padding:'4px 14px', borderRadius:8 }}>
      {grade}
    </span>
  );
}

const inp = { width:'100%', border:`1px solid ${C.border}`, borderRadius:8, padding:'9px 12px', fontSize:13, color:C.text, background:C.input, outline:'none', fontFamily:'inherit', boxSizing:'border-box' };

export default function ContentEngine() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [tab, setTab] = useState("optimize");

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [keyword, setKeyword] = useState("");
  const [optimizeResult, setOptimizeResult] = useState(null);
  const [optimizing, setOptimizing] = useState(false);

  const [scoreUrl, setScoreUrl] = useState("");
  const [scoreKeyword, setScoreKeyword] = useState("");
  const [scoreResult, setScoreResult] = useState(null);
  const [scoring, setScoring] = useState(false);

  const [rewriteTitle, setRewriteTitle] = useState("");
  const [rewriteKeyword, setRewriteKeyword] = useState("");
  const [rewriteResult, setRewriteResult] = useState(null);
  const [rewriting, setRewriting] = useState(false);

  const handleOptimize = async () => {
    if (!title.trim()) return;
    setOptimizing(true); setOptimizeResult(null);
    try {
      const result = await apiClient.post("/seo/optimize-post", { title, content, target_keyword: keyword || null, language: "en" });
      setOptimizeResult(result); toast.success("Post optimized!");
    } catch (err) { toast.error(err.message || "Optimization failed"); }
    finally { setOptimizing(false); }
  };

  const handleScore = async () => {
    if (!scoreUrl.trim()) return;
    setScoring(true); setScoreResult(null);
    try {
      const result = await apiClient.post("/seo/score", { url: scoreUrl, target_keyword: scoreKeyword || null });
      setScoreResult(result); toast.success(`Content scored: ${result.grade}`);
    } catch (err) { toast.error(err.message || "Scoring failed"); }
    finally { setScoring(false); }
  };

  const handleRewrite = async () => {
    if (!rewriteTitle.trim()) return;
    setRewriting(true); setRewriteResult(null);
    try {
      const result = await apiClient.post("/seo/rewrite-title", { title: rewriteTitle, keyword: rewriteKeyword || null, language: "en" });
      setRewriteResult(result); toast.success("Title rewritten!");
    } catch (err) { toast.error(err.message || "Rewrite failed"); }
    finally { setRewriting(false); }
  };

  const tabs = [
    { id:"optimize", label:"Optimize Post" },
    { id:"score",    label:"Content Score" },
    { id:"rewrite",  label:"Title Rewriter" },
  ];

  const scoreBarColor = (score) => {
    if (score >= 80) return '#4ade80';
    if (score >= 65) return '#60a5fa';
    if (score >= 50) return '#fbbf24';
    if (score >= 35) return '#fb923c';
    return '#f87171';
  };

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:"'IBM Plex Sans', system-ui, sans-serif", color:C.text }}>
      <header style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, position:'sticky', top:0, zIndex:10 }}>
        <div style={{ maxWidth:896, margin:'0 auto', padding:'0 24px' }}>
          <div style={{ height:52, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:16 }}>
              <button onClick={() => navigate("/dashboard")} style={{ fontSize:13, color:C.muted, background:'transparent', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                ← Dashboard
              </button>
              <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, color:C.violet, margin:0 }}>SEO Content Engine</h1>
            </div>
          </div>
          <div style={{ display:'flex', borderTop:`1px solid ${C.border}` }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ padding:'10px 20px', fontSize:13, fontWeight:500, background:'transparent', border:'none', borderBottom:`2px solid ${tab===t.id?C.violet:'transparent'}`, color:tab===t.id?C.violet:C.muted, cursor:'pointer' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main style={{ maxWidth:896, margin:'0 auto', padding:'24px' }}>

        {/* OPTIMIZE TAB */}
        {tab === "optimize" && (
          <>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:24, marginBottom:20 }}>
              <h3 style={{ fontSize:14, fontWeight:600, color:C.muted, margin:'0 0 16px' }}>Optimize a blog post for SEO</h3>
              <input value={title} onChange={e=>setTitle(e.target.value)} placeholder='Post title (e.g. CRAZY Comfort Dinner Recipes)'
                style={{ ...inp, marginBottom:12 }}
                onFocus={e=>e.target.style.borderColor=C.violet} onBlur={e=>e.target.style.borderColor=C.border} />
              <textarea value={content} onChange={e=>setContent(e.target.value)}
                placeholder="Paste post content here (optional but recommended for better results)..."
                style={{ ...inp, height:100, resize:'none', marginBottom:12 }}
                onFocus={e=>e.target.style.borderColor=C.violet} onBlur={e=>e.target.style.borderColor=C.border} />
              <div style={{ display:'flex', gap:12, alignItems:'flex-end' }}>
                <input value={keyword} onChange={e=>setKeyword(e.target.value)} placeholder="Target keyword (optional)"
                  style={{ ...inp, flex:1 }}
                  onFocus={e=>e.target.style.borderColor=C.violet} onBlur={e=>e.target.style.borderColor=C.border} />
                <button onClick={handleOptimize} disabled={optimizing || !title.trim()}
                  style={{ padding:'9px 20px', background:C.grad, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:optimizing||!title.trim()?'not-allowed':'pointer', opacity:optimizing||!title.trim()?0.5:1, whiteSpace:'nowrap', fontFamily:'inherit' }}>
                  {optimizing ? "Optimizing..." : "Optimize"}
                </button>
              </div>
            </div>

            {optimizeResult && (
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:24 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                  <h3 style={{ fontSize:14, fontWeight:600, color:C.muted, margin:0 }}>Optimization Result</h3>
                  <ScoreBadge score={optimizeResult.title_score} />
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  <div>
                    <p style={{ fontSize:11, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Original title</p>
                    <p style={{ fontSize:13, color:C.hint, textDecoration:'line-through', margin:0 }}>{optimizeResult.original_title}</p>
                  </div>
                  <div>
                    <p style={{ fontSize:11, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Optimized title</p>
                    <p style={{ fontSize:13, fontWeight:600, color:C.green, background:'rgba(74,222,128,0.08)', padding:'8px 12px', borderRadius:7, margin:0 }}>{optimizeResult.optimized_title}</p>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                    <div>
                      <p style={{ fontSize:11, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Primary keyword</p>
                      <span style={{ fontSize:13, fontWeight:600, background:'rgba(96,165,250,0.12)', color:C.blue, padding:'4px 10px', borderRadius:6 }}>{optimizeResult.primary_keyword}</span>
                    </div>
                    <div>
                      <p style={{ fontSize:11, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Slug</p>
                      <p style={{ fontSize:13, fontFamily:"'JetBrains Mono', monospace", background:C.input, padding:'4px 10px', borderRadius:6, color:C.muted, margin:0 }}>/{optimizeResult.optimized_slug}</p>
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize:11, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Meta description</p>
                    <p style={{ fontSize:13, color:C.muted, background:C.input, padding:'8px 12px', borderRadius:7, margin:0 }}>{optimizeResult.meta_description}</p>
                  </div>
                  <div>
                    <p style={{ fontSize:11, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 6px' }}>Secondary keywords</p>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                      {optimizeResult.secondary_keywords.map((kw, i) => (
                        <span key={i} style={{ fontSize:12, background:C.input, color:C.muted, padding:'3px 10px', borderRadius:5 }}>{kw}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize:11, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 6px' }}>Improvements made</p>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      {optimizeResult.improvements.map((imp, i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:C.green }}>
                          <span style={{ width:6, height:6, borderRadius:'50%', background:C.green, flexShrink:0 }} />{imp}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* SCORE TAB */}
        {tab === "score" && (
          <>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:24, marginBottom:20 }}>
              <h3 style={{ fontSize:14, fontWeight:600, color:C.muted, margin:'0 0 16px' }}>Score any page's SEO content</h3>
              <div style={{ display:'flex', gap:12, alignItems:'flex-end' }}>
                <input value={scoreUrl} onChange={e=>setScoreUrl(e.target.value)} placeholder="Enter URL to analyze (e.g. https://example.com/blog-post)"
                  style={{ ...inp, flex:1 }}
                  onFocus={e=>e.target.style.borderColor=C.violet} onBlur={e=>e.target.style.borderColor=C.border} />
                <input value={scoreKeyword} onChange={e=>setScoreKeyword(e.target.value)} placeholder="Target keyword (optional)"
                  style={{ ...inp, width:180 }}
                  onFocus={e=>e.target.style.borderColor=C.violet} onBlur={e=>e.target.style.borderColor=C.border} />
                <button onClick={handleScore} disabled={scoring || !scoreUrl.trim()}
                  style={{ padding:'9px 20px', background:C.grad, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:scoring||!scoreUrl.trim()?'not-allowed':'pointer', opacity:scoring||!scoreUrl.trim()?0.5:1, whiteSpace:'nowrap', fontFamily:'inherit' }}>
                  {scoring ? "Scoring..." : "Score"}
                </button>
              </div>
            </div>

            {scoreResult && (
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:24 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                  <h3 style={{ fontSize:14, fontWeight:600, color:C.muted, margin:0 }}>Content Score</h3>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <GradeBadge grade={scoreResult.grade} />
                    <ScoreBadge score={scoreResult.overall_score} />
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
                  {[
                    { label:"Words",          value:scoreResult.content_length,          ok:scoreResult.content_length_ok },
                    { label:"H2 headings",    value:scoreResult.h2_count,                ok:scoreResult.h2_count >= 3 },
                    { label:"Images",         value:scoreResult.image_count,             ok:scoreResult.image_count >= 1 },
                    { label:"Internal links", value:scoreResult.internal_links_count,    ok:scoreResult.internal_links_count >= 3 },
                    { label:"Readability",    value:scoreResult.readability_score,       ok:scoreResult.readability_score >= 60 },
                    { label:"Keyword density",value:`${scoreResult.keyword_density}%`,  ok:scoreResult.keyword_density >= 1 && scoreResult.keyword_density <= 3 },
                    { label:"Keyword in title",value:scoreResult.keyword_in_title?"Yes":"No", ok:scoreResult.keyword_in_title },
                    { label:"Title length",   value:scoreResult.title_length_ok?"OK":"Fix", ok:scoreResult.title_length_ok },
                  ].map((m, i) => (
                    <div key={i} style={{ borderRadius:8, border:`1px solid ${m.ok?'rgba(74,222,128,0.3)':'rgba(248,113,113,0.3)'}`, background:m.ok?'rgba(74,222,128,0.07)':'rgba(248,113,113,0.07)', padding:'10px 12px', textAlign:'center' }}>
                      <div style={{ fontSize:18, fontWeight:800, color:m.ok?C.green:C.red }}>{m.value}</div>
                      <div style={{ fontSize:11, color:C.hint, marginTop:3 }}>{m.label}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <h4 style={{ fontSize:13, fontWeight:600, color:C.muted, margin:'0 0 8px' }}>Recommendations</h4>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {scoreResult.recommendations.map((rec, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, fontSize:13 }}>
                        <span style={{ color:'#fbbf24', marginTop:1 }}>⚡</span>
                        <span style={{ color:C.muted }}>{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* REWRITE TAB */}
        {tab === "rewrite" && (
          <>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:24, marginBottom:20 }}>
              <h3 style={{ fontSize:14, fontWeight:600, color:C.muted, margin:'0 0 16px' }}>Rewrite a title for SEO</h3>
              <div style={{ display:'flex', gap:12, alignItems:'flex-end' }}>
                <input value={rewriteTitle} onChange={e=>setRewriteTitle(e.target.value)} placeholder="Enter title to rewrite (e.g. CRAZY Ultimate Weight Loss Hacks)"
                  style={{ ...inp, flex:1 }}
                  onFocus={e=>e.target.style.borderColor=C.violet} onBlur={e=>e.target.style.borderColor=C.border} />
                <input value={rewriteKeyword} onChange={e=>setRewriteKeyword(e.target.value)} placeholder="Target keyword (optional)"
                  style={{ ...inp, width:180 }}
                  onFocus={e=>e.target.style.borderColor=C.violet} onBlur={e=>e.target.style.borderColor=C.border} />
                <button onClick={handleRewrite} disabled={rewriting || !rewriteTitle.trim()}
                  style={{ padding:'9px 20px', background:C.grad, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:rewriting||!rewriteTitle.trim()?'not-allowed':'pointer', opacity:rewriting||!rewriteTitle.trim()?0.5:1, whiteSpace:'nowrap', fontFamily:'inherit' }}>
                  {rewriting ? "Rewriting..." : "Rewrite"}
                </button>
              </div>
            </div>

            {rewriteResult && (
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:24 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                  <h3 style={{ fontSize:14, fontWeight:600, color:C.muted, margin:0 }}>Rewrite Result</h3>
                  <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:14 }}>
                    <span style={{ color:C.red, fontWeight:700 }}>{rewriteResult.score_before}</span>
                    <span style={{ color:C.hint }}>→</span>
                    <span style={{ color:C.green, fontWeight:700 }}>{rewriteResult.score_after}</span>
                  </div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  <div>
                    <p style={{ fontSize:11, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Before</p>
                    <p style={{ fontSize:13, color:C.red, textDecoration:'line-through', background:'rgba(248,113,113,0.08)', padding:'8px 12px', borderRadius:7, margin:0 }}>{rewriteResult.original_title}</p>
                  </div>
                  <div>
                    <p style={{ fontSize:11, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>After</p>
                    <p style={{ fontSize:13, fontWeight:600, color:C.green, background:'rgba(74,222,128,0.08)', padding:'8px 12px', borderRadius:7, margin:0 }}>{rewriteResult.rewritten_title}</p>
                  </div>
                  <div>
                    <p style={{ fontSize:11, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Keyword</p>
                    <span style={{ fontSize:13, fontWeight:600, background:'rgba(96,165,250,0.12)', color:C.blue, padding:'4px 10px', borderRadius:6 }}>{rewriteResult.primary_keyword}</span>
                  </div>
                  <div>
                    <p style={{ fontSize:11, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 6px' }}>Changes</p>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      {rewriteResult.changes_made.map((c, i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:C.green }}>
                          <span style={{ width:6, height:6, borderRadius:'50%', background:C.green, flexShrink:0 }} />{c}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
